import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';
import * as QRCode from 'qrcode';
import React, { useEffect, useState } from 'react';
import toast from './Toast';

const { REACT_APP_BASE_URL } = process.env;

export default function Modal(props: any) {
    const { onClose, open, message, component, details } = props;
    const urlToCopy = `${window.location.href.split('/')[0]}//${
        window.location.href.split('/')[2]
    }/viewExperiences/${details?.type}/${details?.id}`;
    const calendarURL = `${REACT_APP_BASE_URL}/calendar/${details?.calendarId}.ics`;
    const [qrCode, setQrCode] = useState('');

    useEffect(() => {
        const generateQrCode = async () => {
            const qr = await QRCode.toDataURL(urlToCopy, {
                errorCorrectionLevel: 'H', // high — survives partial damage on physical labels
                width: 512,
            });
            setQrCode(qr);
        };
        generateQrCode();
    }, [urlToCopy, setQrCode]);

    function handleQrDownload() {
        const link = document.createElement('a');
        link.href = qrCode;
        link.download = `QR Code - ${details?.name}.png`;
        link.click();
    }

    async function copyTextToClipboard(urlRef: any) {
        if ('clipboard' in navigator) {
            return await navigator.clipboard.writeText(urlRef);
        } else {
            return document.execCommand('copy', true, urlRef);
        }
    }

    const handleCopyClick = () => {
        copyTextToClipboard(urlToCopy)
            .then(() => {
                toast.success('COPIED');
            })
            .catch((err) => {
                console.error('Failed to copy text:', err);
                toast.error('Failed to copy. Please try again.');
            });
    };

    const handleCalendarCopyClick = () => {
        copyTextToClipboard(calendarURL)
            .then(() => {
                toast.success('COPIED');
            })
            .catch((err) => {
                console.error('Failed to copy text:', err);
                toast.error('Failed to copy. Please try again.');
            });
    };

    const handleClose = () => {
        onClose();
    };

    const toTitle = (value?: string) =>
        value
            ? String(value)
                  .replace(/_/g, ' ')
                  .replace(/\b\w/g, (c) => c.toUpperCase())
            : '';

    const escapeHtml = (value: any) =>
        String(value ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');

    const formatCurrency = (value: any) => {
        const n = Number(value);
        if (Number.isNaN(n)) return '';
        return `$${n.toLocaleString()}`;
    };

    const handleDownloadExperiencePdf = () => {
        const fullAddress = [
            details?.streetAddress,
            details?.suburb,
            details?.postcode,
            details?.state,
            details?.countryCode,
        ]
            .filter(Boolean)
            .join(' ');

        const rows = [
            { label: 'Experience', value: details?.name },
            { label: 'Type', value: toTitle(details?.type) },
            { label: 'Address', value: fullAddress },
            { label: 'Hosted by', value: details?.owner?.name },
            { label: 'Max guests', value: details?.details?.maxGuests },
            { label: 'Sleeps', value: details?.details?.overnightGuests },
            { label: 'Bedrooms', value: details?.details?.bedrooms },
            { label: 'Beds', value: details?.details?.beds },
            { label: 'Bathrooms', value: details?.details?.bathrooms },
            {
                label: 'Base price',
                value: details?.details?.basePrice
                    ? formatCurrency(details.details.basePrice)
                    : '',
            },
            { label: 'Description', value: details?.description },
        ].filter(
            (row) =>
                row.value !== undefined &&
                row.value !== null &&
                row.value !== '',
        );

        const summaryRowsHtml = rows
            .map(
                (row) =>
                    `<div class="row"><span class="label">${escapeHtml(
                        row.label,
                    )}:</span> <span class="value">${escapeHtml(
                        row.value,
                    )}</span></div>`,
            )
            .join('');

        const photoSources = (
            Array.isArray(details?.photos) ? details.photos : []
        )
            .map((p: any) => p?.src || p?.url)
            .filter(Boolean);
        const fallbackPhoto =
            details?.photo || details?.image?.src || details?.image?.url;
        if (photoSources.length === 0 && fallbackPhoto)
            photoSources.push(fallbackPhoto);

        const photoHtml = photoSources.length
            ? `<section class="section">
                <h3>Photos</h3>
                <div class="gallery">
                    ${photoSources
                        .slice(0, 8)
                        .map(
                            (src: string) =>
                                `<div class="image-wrap"><img src="${escapeHtml(src)}" alt="Experience" /></div>`,
                        )
                        .join('')}
                </div>
            </section>`
            : '';

        const charges = Array.isArray(details?.charges) ? details.charges : [];
        const pricingRows = [
            ...charges
                .filter((c: any) => c?.chargeType === 'price')
                .map((c: any) => ({
                    label: c?.name ? `Base price (${c.name})` : 'Base price',
                    value: c?.basePrice ?? c?.unitPrice,
                })),
            ...charges
                .filter((c: any) => c?.chargeType === 'fee')
                .map((c: any) => ({
                    label: c?.name || c?.feeName || 'Fee',
                    value: c?.unitPrice ?? c?.basePrice,
                })),
            ...charges
                .filter((c: any) => c?.chargeType === 'cleaningFee')
                .map((c: any) => ({
                    label: `Cleaning fee (${toTitle(c?.cleaningFeeType || 'standard')})`,
                    value: c?.basePrice ?? c?.unitPrice,
                })),
        ]
            .filter(
                (r) =>
                    r.value !== undefined && r.value !== null && r.value !== '',
            )
            .map(
                (r) =>
                    `<div class="row"><span class="label">${escapeHtml(
                        r.label,
                    )}:</span> <span class="value">${escapeHtml(
                        typeof r.value === 'number'
                            ? formatCurrency(r.value)
                            : r.value,
                    )}</span></div>`,
            )
            .join('');

        const spaces = Array.isArray(details?.spaces) ? details.spaces : [];
        const spacesHtml = spaces.length
            ? spaces
                  .map((space: any) => {
                      const bullets = [
                          space?.spaceType
                              ? `Type: ${toTitle(space.spaceType)}`
                              : '',
                          space?.seatingCapacity
                              ? `Seating: ${space.seatingCapacity}`
                              : '',
                          space?.parkingCapacity
                              ? `Parking: ${space.parkingCapacity}`
                              : '',
                          space?.desk ? `Desks: ${space.desk}` : '',
                          space?.beds ? `Beds: ${space.beds}` : '',
                      ]
                          .filter(Boolean)
                          .join(' | ');
                      return `<div class="row"><span class="label">${escapeHtml(
                          space?.name || 'Space',
                      )}:</span> <span class="value">${escapeHtml(
                          bullets || 'Configured',
                      )}</span></div>`;
                  })
                  .join('')
            : '';

        const detailsObj = details?.details || {};
        const amenityExcludeKeys = new Set([
            'desks',
            'privateOffices',
            'meetingRooms',
            'basePrice',
            'beds',
            'bedrooms',
            'baths',
            'bathrooms',
            'overnightGuests',
            'maxGuests',
            'rules',
            'subType',
            'dropoffStreetAddress',
            'dropoffSuburb',
            'dropoffPostcode',
            'dropoffState',
            'dropoffCountryCode',
        ]);
        const amenities = Object.entries(detailsObj)
            .filter(
                ([key, value]) =>
                    !amenityExcludeKeys.has(key) &&
                    (value === true ||
                        (typeof value === 'number' && value > 0) ||
                        (Array.isArray(value) && value.length > 0)),
            )
            .map(([key]) => toTitle(key));
        const amenitiesHtml = amenities.length
            ? `<div class="chips">${amenities
                  .map((a) => `<span class="chip">${escapeHtml(a)}</span>`)
                  .join('')}</div>`
            : '';

        const policy = details?.policy;
        const policyHtml = policy
            ? `<div class="row"><span class="label">${escapeHtml(
                  policy?.name || 'Policy',
              )}:</span> <span class="value">${escapeHtml(
                  policy?.description || 'Applied',
              )}</span></div>`
            : `<div class="row"><span class="label">Cancellation policy:</span> <span class="value">Fair Cancellation Policy</span></div>`;

        const printFrame = document.createElement('iframe');
        printFrame.style.position = 'fixed';
        printFrame.style.right = '0';
        printFrame.style.bottom = '0';
        printFrame.style.width = '0';
        printFrame.style.height = '0';
        printFrame.style.border = '0';
        document.body.appendChild(printFrame);

        const frameDoc = printFrame.contentWindow?.document;
        const frameWindow = printFrame.contentWindow;
        if (!frameDoc || !frameWindow) {
            printFrame.remove();
            return;
        }

        frameDoc.open();
        frameDoc.write(`
            <html>
            <head>
                <title>${escapeHtml(details?.name || 'Experience Details')}</title>
                <style>
                    body { font-family: Inter, Arial, sans-serif; margin: 24px; color: #1f3550; }
                    h1 { margin: 0 0 6px 0; font-size: 24px; }
                    h2 { margin: 0 0 18px 0; font-size: 14px; font-weight: 500; color: #5f7489; }
                    h3 { margin: 0 0 10px 0; font-size: 15px; }
                    .section { margin: 0 0 16px 0; }
                    .gallery { display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 10px; }
                    .image-wrap { border: 1px solid #d7e0e8; border-radius: 10px; overflow: hidden; }
                    .image-wrap img { width: 100%; height: 180px; object-fit: cover; display: block; }
                    .summary { padding: 12px 14px; border: 1px solid #d7e0e8; border-radius: 8px; }
                    .row { margin: 0 0 8px 0; line-height: 1.4; }
                    .label { font-weight: 700; }
                    .chips { display: flex; flex-wrap: wrap; gap: 8px; }
                    .chip { display: inline-flex; align-items: center; border: 1px solid #d7e0e8; border-radius: 999px; padding: 4px 10px; font-size: 12px; }
                </style>
            </head>
            <body>
                <h1>Experience Details</h1>
                <h2>${escapeHtml(details?.name || '')}</h2>
                <section class="summary section">
                    ${summaryRowsHtml}
                </section>
                ${photoHtml}
                ${
                    pricingRows
                        ? `<section class="summary section"><h3>Pricing & Fees</h3>${pricingRows}</section>`
                        : ''
                }
                ${
                    spacesHtml
                        ? `<section class="summary section"><h3>Space Configurations</h3>${spacesHtml}</section>`
                        : ''
                }
                ${
                    amenitiesHtml
                        ? `<section class="summary section"><h3>Amenities</h3>${amenitiesHtml}</section>`
                        : ''
                }
                <section class="summary section"><h3>Policies</h3>${policyHtml}</section>
            </body>
            </html>
        `);
        frameDoc.close();

        const cleanup = () => {
            setTimeout(() => {
                printFrame.remove();
            }, 0);
        };
        frameWindow.onafterprint = cleanup;
        setTimeout(() => {
            frameWindow.focus();
            frameWindow.print();
        }, 150);
        setTimeout(cleanup, 2000);
    };

    const canShare = component === 'Share' && details?.isAvailable !== false;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
            <DialogTitle>
                {message}
                <IconButton
                    onClick={handleClose}
                    size='small'
                    aria-label='close'
                    sx={{ color: 'text.primary' }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                {component === 'Share' && details?.isAvailable === false ? (
                    <DialogContentText>
                        You cannot share a listing that is unavailable.
                    </DialogContentText>
                ) : null}

                {canShare ? (
                    <DialogContentText>
                        This link can only be shared with other 7DAY users.
                    </DialogContentText>
                ) : null}

                {component === 'Calendar' ? (
                    <Stack spacing={2}>
                        <DialogContentText>
                            The following link can be used to add the
                            reservations of this experience to an external
                            calendar. Each experience will have its own unique
                            link that will need to be added to the external
                            calendar in order to sync the reservations.
                        </DialogContentText>
                        <DialogContentText>
                            Please Note: This calendar contains the approved and
                            completed reservations of each experience and is
                            refreshed at a frequency defined by the destination
                            calendars (Google, Outlook,..). We do not allow the
                            ability to import reservations from another
                            calendar.
                        </DialogContentText>
                    </Stack>
                ) : null}

                {component === 'Team change' ? (
                    <DialogContentText>
                        This user is a Team Admin. By changing this user's Team,
                        they will no longer be able to see and manage other
                        users under that team.
                    </DialogContentText>
                ) : null}

                {component === 'QR' ? (
                    <Stack spacing={2} alignItems='center'>
                        <DialogContentText>
                            Download and place the QR code on or near the
                            vehicle to help guests instantly find and book it.
                        </DialogContentText>
                        {qrCode ? (
                            <Box
                                component='img'
                                src={qrCode}
                                alt='Vehicle QR code'
                                sx={{ width: 250 }}
                            />
                        ) : null}
                        <Typography variant='body1'>{urlToCopy}</Typography>
                    </Stack>
                ) : null}
            </DialogContent>
            <DialogActions>
                {canShare ? (
                    <>
                        <Button
                            startIcon={<ContentCopyIcon />}
                            onClick={handleCopyClick}
                            variant='outlined'
                        >
                            Copy Link
                        </Button>
                        <Button
                            startIcon={<PictureAsPdfOutlinedIcon />}
                            onClick={handleDownloadExperiencePdf}
                            variant='outlined'
                        >
                            Download PDF
                        </Button>
                    </>
                ) : null}
                {component === 'Calendar' ? (
                    <Button
                        startIcon={<ContentCopyIcon />}
                        onClick={handleCalendarCopyClick}
                    >
                        Copy Link
                    </Button>
                ) : null}
                {component === 'QR' ? (
                    <>
                        <Button
                            startIcon={<FileDownloadIcon />}
                            onClick={handleQrDownload}
                        >
                            Download PNG
                        </Button>
                        <Button
                            startIcon={<ContentCopyIcon />}
                            onClick={handleCopyClick}
                        >
                            Copy Link
                        </Button>
                    </>
                ) : null}
                <Button variant='contained' onClick={handleClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}
